/**
 * Context + hooks de autenticación.
 *
 * Expone:
 * - `<AuthProvider>` — envuelve la app, mantiene `session` y `user` en state.
 * - `useAuth()` — hook para leer session/user/isLoading/signIn/signOut.
 *
 * Sincroniza con Supabase JS: al montar, pide la sesión persistida en
 * localStorage; se suscribe a cambios (login/logout/refresh) vía
 * `onAuthStateChange` y actualiza el state.
 */
import type { Session, User } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { track } from "./analytics";
import { supabase } from "./supabase";

/**
 * Clave en `sessionStorage` con el `user.id` del último `user_logged_in`
 * trackeado en este tab. Sirve para deduplicar el evento contra:
 *
 * - Rehidratación de sesión al cargar la página (Supabase reemite
 *   `SIGNED_IN` con la sesión persistida en `localStorage`).
 * - Token refresh automático (el SDK emite `TOKEN_REFRESHED`, pero
 *   también `SIGNED_IN` en algunas versiones del SDK).
 * - Cross-tab sync (otra pestaña refresca y emite el evento).
 *
 * sessionStorage es per-tab — sí queremos un `user_logged_in` por
 * pestaña nueva (es información útil: "este user abrió N pestañas
 * activas"). Lo que queremos cortar es N eventos por la MISMA sesión
 * del navegador.
 */
const LOGGED_IN_TRACKED_KEY = "kaptar:user_logged_in_tracked_user_id";

function markLoginTrackedAndReturnShouldFire(userId: string): boolean {
  // Si sessionStorage no está disponible (Safari private, SSR, etc.)
  // preferimos trackear de más que perder el evento, así que firmamos
  // como "primera vez".
  try {
    const previous = window.sessionStorage.getItem(LOGGED_IN_TRACKED_KEY);
    if (previous === userId) return false;
    window.sessionStorage.setItem(LOGGED_IN_TRACKED_KEY, userId);
    return true;
  } catch {
    return true;
  }
}

function clearLoginTrackingMark(): void {
  try {
    window.sessionStorage.removeItem(LOGGED_IN_TRACKED_KEY);
  } catch {
    // ignore
  }
}

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  /** true mientras se resuelve la sesión inicial desde localStorage. */
  isLoading: boolean;
  /**
   * Inicia sesión con email + password.
   *
   * `captchaToken` viene del widget Cloudflare Turnstile (R-SEC-014).
   * Si Supabase Auth tiene captcha activado en el Dashboard y el token
   * está ausente o es inválido, Supabase responde con error. En dev
   * sin Turnstile setup, pasar `null` y Supabase decide según su config.
   */
  signInWithPassword: (
    email: string,
    password: string,
    captchaToken?: string | null,
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // 1. Sesión inicial (recupera de localStorage si existe).
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data.session);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    // 2. Subscribe a cambios (login/logout/token-refresh).
    //
    // Sprint 12 · 1: emitimos `user_logged_in` / `user_logged_out` /
    // `user_signed_up` desde acá porque es el único punto que ve todos
    // los caminos de auth (password, magic link, OAuth en el futuro,
    // refresh, etc.).
    //
    // Heurística para signup vs login:
    // Supabase emite SIGNED_IN tanto en login como justo después del
    // signup. Distinguimos comparando `user.created_at` con `now`:
    // si fue hace <60s, es un signup; si no, es un login regular.
    //
    // Deduplicación contra ruido (fix mayo 28, 2026):
    // El listener `onAuthStateChange` re-emite `SIGNED_IN` en cada
    // rehidratación de la sesión persistida (page reload) y, según la
    // versión del SDK, también tras `TOKEN_REFRESHED` cada ~60min.
    // Sin dedup, `user_logged_in` se trackeaba ~N veces por sesión real.
    // - Ignoramos `INITIAL_SESSION`, `TOKEN_REFRESHED`, `USER_UPDATED`
    //   y `PASSWORD_RECOVERY` — no son login real.
    // - Para `SIGNED_IN`, dedupeamos por `user.id` vía sessionStorage:
    //   solo trackeamos la primera vez que vemos a este user en esta
    //   pestaña.
    // - `SIGNED_OUT` limpia el flag, así un re-login en la misma pestaña
    //   se trackea correctamente.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession);

      if (
        event === "INITIAL_SESSION" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED" ||
        event === "PASSWORD_RECOVERY"
      ) {
        return;
      }

      if (event === "SIGNED_IN" && newSession?.user) {
        if (!markLoginTrackedAndReturnShouldFire(newSession.user.id)) {
          return;
        }
        const createdAt = Date.parse(newSession.user.created_at);
        const isFreshSignup = Number.isFinite(createdAt) && Date.now() - createdAt < 60_000;
        if (isFreshSignup) {
          track({ event_name: "user_signed_up" });
        } else {
          track({ event_name: "user_logged_in" });
        }
      } else if (event === "SIGNED_OUT") {
        clearLoginTrackingMark();
        track({ event_name: "user_logged_out" });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signInWithPassword = useCallback(
    async (email: string, password: string, captchaToken?: string | null) => {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        // Solo incluimos options si hay token — Supabase JS lo ignora si
        // captcha no está activado en Dashboard, pero mantenemos el
        // payload limpio cuando no aplica.
        ...(captchaToken ? { options: { captchaToken } } : {}),
      });
      return { error: error?.message ?? null };
    },
    [],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      isLoading,
      signInWithPassword,
      signOut,
    }),
    [session, isLoading, signInWithPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook + provider pareja, no vale la pena separarlos
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error(
      "useAuth() debe usarse dentro de <AuthProvider>. " +
        "Verifica que App.tsx envuelva las rutas con AuthProvider.",
    );
  }
  return ctx;
}
